import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const TakumiGroupDoubts = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [actionMessage, setActionMessage] = useState({ text: '', type: '' });

  // Modal edit form state
  const [editStatus, setEditStatus] = useState('scheduled');
  const [editMeetLink, setEditMeetLink] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSlot, setEditSlot] = useState('');

  const itemsPerPage = 10;

  const fetchDoubtRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/takumi/group-doubt/all`);
      if (!response.ok) {
        throw new Error(`Failed to fetch group doubt requests (HTTP ${response.status})`);
      }

      const result = await response.json();
      if (result.success) {
        setRequests(result.data || []);
      } else {
        setError(result.message || 'Failed to retrieve doubt requests');
      }
    } catch (err) {
      console.error('Error fetching group doubt requests:', err);
      setError(err.message || 'Failed to connect to group doubt server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoubtRequests();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'received':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completed':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'cancelled':
        return 'bg-rose-100 text-rose-800 border-rose-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusCount = (status) => {
    if (status === 'all') return requests.length;
    return requests.filter((r) => r.status === status).length;
  };

  // Open manage modal
  const openModal = (request) => {
    setSelectedRequest(request);
    setEditStatus(request.status || 'scheduled');
    setEditMeetLink(request.meetLink || '');
    setEditDate(request.scheduledDate || '');
    setEditTime(request.scheduledTime || '');
    setEditNotes(request.adminNotes || '');
    setEditSlot(request.preferredSlot || '');
    setActionMessage({ text: '', type: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setActionMessage({ text: '', type: '' });
  };

  // Update doubt request
  const handleUpdateRequest = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;

    try {
      setUpdating(true);
      setActionMessage({ text: '', type: '' });

      const targetId = selectedRequest._id || selectedRequest.requestId;
      const response = await fetch(`${API_BASE_URL}/api/takumi/group-doubt/${targetId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: editStatus,
          meetLink: editMeetLink,
          scheduledDate: editDate,
          scheduledTime: editTime,
          adminNotes: editNotes,
          preferredSlot: editSlot
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to update doubt request');
      }

      setActionMessage({ text: 'Group doubt session updated successfully!', type: 'success' });

      // Update local state
      setRequests((prev) =>
        prev.map((r) =>
          (r._id === selectedRequest._id || r.requestId === selectedRequest.requestId)
            ? {
                ...r,
                status: editStatus,
                meetLink: editMeetLink,
                scheduledDate: editDate,
                scheduledTime: editTime,
                adminNotes: editNotes,
                preferredSlot: editSlot,
                updatedAt: new Date()
              }
            : r
        )
      );

      setSelectedRequest((prev) => ({
        ...prev,
        status: editStatus,
        meetLink: editMeetLink,
        scheduledDate: editDate,
        scheduledTime: editTime,
        adminNotes: editNotes,
        preferredSlot: editSlot,
        updatedAt: new Date()
      }));
    } catch (err) {
      console.error('Error updating doubt request:', err);
      setActionMessage({ text: err.message || 'Failed to update doubt request', type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  // Delete doubt request
  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this doubt session request?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/takumi/group-doubt/${requestId}`, {
        method: 'DELETE'
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to delete doubt request');
      }

      setRequests((prev) => prev.filter((r) => r._id !== requestId && r.requestId !== requestId));
      if (selectedRequest && (selectedRequest._id === requestId || selectedRequest.requestId === requestId)) {
        closeModal();
      }
    } catch (err) {
      alert(err.message || 'Error deleting doubt request');
    }
  };

  // Filtered requests
  const filtered = requests.filter((req) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (req.fullName && req.fullName.toLowerCase().includes(term)) ||
      (req.email && req.email.toLowerCase().includes(term)) ||
      (req.phone && req.phone.includes(term)) ||
      (req.topic && req.topic.toLowerCase().includes(term)) ||
      (req.requestId && req.requestId.toLowerCase().includes(term));

    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchesExp = experienceFilter === 'all' || req.experienceLevel === experienceFilter;

    return matchesSearch && matchesStatus && matchesExp;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filtered.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex flex-col justify-center items-center h-64 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <p className="text-gray-500 font-medium text-sm">Loading Takumi Doubt Clearing Class requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-3xl">🙋‍♂️</span>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Takumi Doubt Classes</h1>
              <p className="text-sm text-gray-500 mt-1">
                View student doubt submissions, schedule group mentoring sessions, and provide meeting invites.
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchDoubtRequests}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-all"
          >
            <span>🔄</span> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <p className="text-sm font-medium">{error}</p>
          </div>
          <button
            onClick={fetchDoubtRequests}
            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div
          onClick={() => setStatusFilter('all')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'all'
              ? 'bg-gray-900 text-white border-gray-900 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-gray-200 hover:border-gray-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider opacity-75">All Requests</div>
          <div className="text-2xl font-bold mt-1">{getStatusCount('all')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('received')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'received'
              ? 'bg-amber-500 text-white border-amber-500 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-amber-200 hover:border-amber-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 opacity-90">Received</div>
          <div className="text-2xl font-bold mt-1 text-amber-900">{getStatusCount('received')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('scheduled')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'scheduled'
              ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-blue-200 hover:border-blue-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-600 opacity-90">Scheduled</div>
          <div className="text-2xl font-bold mt-1 text-blue-900">{getStatusCount('scheduled')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('completed')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'completed'
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-emerald-200 hover:border-emerald-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 opacity-90">Completed</div>
          <div className="text-2xl font-bold mt-1 text-emerald-900">{getStatusCount('completed')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('cancelled')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'cancelled'
              ? 'bg-rose-600 text-white border-rose-600 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-rose-200 hover:border-rose-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-rose-600 opacity-90">Cancelled</div>
          <div className="text-2xl font-bold mt-1 text-rose-900">{getStatusCount('cancelled')}</div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by student name, email, phone, topic, request ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          />
          <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-xs font-bold"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="received">Received</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={experienceFilter}
            onChange={(e) => {
              setExperienceFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          >
            <option value="all">All Levels</option>
            <option value="Beginner">Beginner</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Advanced">Advanced</option>
          </select>

          {(searchTerm || statusFilter !== 'all' || experienceFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setExperienceFilter('all');
                setCurrentPage(1);
              }}
              className="text-xs text-teal-600 hover:text-teal-800 font-medium px-2 py-2 underline"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tracking ID / Submitted
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Student Info
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Doubt Topic & Questions
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Level & Slot
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status & Meet
                </th>
                <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="text-3xl mb-2">💬</div>
                    <p className="text-base font-medium text-gray-700">No group doubt requests found</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Try adjusting your search criteria or filter options.
                    </p>
                  </td>
                </tr>
              ) : (
                currentItems.map((req) => (
                  <tr key={req._id || req.requestId} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 font-mono text-xs font-bold text-teal-700 bg-teal-50 px-2 py-1 rounded inline-block">
                        {req.requestId}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDate(req.createdAt)}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{req.fullName}</div>
                      <div className="text-xs text-gray-500 flex flex-col mt-0.5">
                        <span>✉️ {req.email}</span>
                        <span>📞 {req.phone}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-sm font-medium text-gray-900 truncate" title={req.topic}>
                        {req.topic}
                      </div>
                      <div className="text-xs text-gray-500 line-clamp-2 mt-0.5" title={req.description}>
                        {req.description}
                      </div>
                      {req.githubOrProjectLink && (
                        <a
                          href={req.githubOrProjectLink.startsWith('http') ? req.githubOrProjectLink : `https://${req.githubOrProjectLink}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-xs text-teal-600 hover:underline mt-1"
                        >
                          🔗 Project / Repo Link
                        </a>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
                        {req.experienceLevel || 'Intermediate'}
                      </span>
                      <div className="text-xs text-gray-500 mt-1 truncate max-w-[150px]" title={req.preferredSlot}>
                        🕒 {req.preferredSlot || 'Weekend Batch'}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                          req.status
                        )}`}
                      >
                        {req.status?.toUpperCase() || 'SCHEDULED'}
                      </span>
                      {req.meetLink && (
                        <div className="mt-1">
                          {req.meetLink.startsWith('http') ? (
                            <a
                              href={req.meetLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline block truncate max-w-[160px]"
                              title={req.meetLink}
                            >
                              🎥 Join Meeting Link
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400 block truncate max-w-[160px]" title={req.meetLink}>
                              {req.meetLink}
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openModal(req)}
                          className="text-xs bg-teal-50 hover:bg-teal-100 text-teal-700 font-semibold px-3 py-1.5 rounded-lg border border-teal-200 transition-colors"
                        >
                          Manage & Schedule
                        </button>
                        <button
                          onClick={() => handleDeleteRequest(req._id || req.requestId)}
                          className="text-xs text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded"
                          title="Delete Request"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {filtered.length > itemsPerPage && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Showing <span className="font-semibold">{startIndex + 1}</span> to{' '}
              <span className="font-semibold">{Math.min(startIndex + itemsPerPage, filtered.length)}</span> of{' '}
              <span className="font-semibold">{filtered.length}</span> entries
            </div>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-xs font-semibold text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Manage & Schedule Doubt Session Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border border-gray-200 animate-fadeIn">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-teal-50/40 rounded-t-2xl">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🙋‍♂️</span>
                  <h2 className="text-xl font-bold text-gray-900">Manage Doubt Session & Schedule</h2>
                </div>
                <p className="text-xs font-mono text-teal-700 mt-1">Request ID: {selectedRequest.requestId}</p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Notification Message */}
              {actionMessage.text && (
                <div
                  className={`p-3 rounded-lg text-sm font-medium ${
                    actionMessage.type === 'success'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-rose-50 text-rose-800 border border-rose-200'
                  }`}
                >
                  {actionMessage.text}
                </div>
              )}

              {/* Student Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Student Name</div>
                  <div className="text-base font-bold text-gray-900">{selectedRequest.fullName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Student Email</div>
                  <div className="text-sm font-medium text-gray-900">{selectedRequest.email}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Contact Phone</div>
                  <div className="text-sm font-medium text-gray-900">{selectedRequest.phone}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Experience Level</div>
                  <div className="text-sm font-bold text-teal-700">{selectedRequest.experienceLevel || 'Intermediate'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Plan Enrolled</div>
                  <div className="text-sm font-medium text-gray-700">
                    {selectedRequest.planName || selectedRequest.plan || 'Takumi Edge'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Submitted Date</div>
                  <div className="text-sm text-gray-700">{formatDate(selectedRequest.createdAt)}</div>
                </div>
              </div>

              {/* Topic & Detailed Doubt */}
              <div className="space-y-3 bg-teal-50/40 p-4 rounded-xl border border-teal-100">
                <div>
                  <div className="text-xs font-semibold uppercase text-teal-800 mb-1">Doubt Topic</div>
                  <div className="text-base font-bold text-gray-900">{selectedRequest.topic}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-teal-800 mb-1">Detailed Description</div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap bg-white p-3 rounded-lg border border-teal-200">
                    {selectedRequest.description}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs pt-1">
                  <span className="text-gray-600">
                    Preferred Slot: <strong className="text-gray-900">{selectedRequest.preferredSlot || 'Weekend Batch'}</strong>
                  </span>
                  {selectedRequest.githubOrProjectLink && (
                    <a
                      href={selectedRequest.githubOrProjectLink.startsWith('http') ? selectedRequest.githubOrProjectLink : `https://${selectedRequest.githubOrProjectLink}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-700 font-semibold underline hover:text-teal-900"
                    >
                      Open GitHub / Project Link ↗
                    </a>
                  )}
                </div>
              </div>

              {/* Scheduling & Mentor Management Form */}
              <form onSubmit={handleUpdateRequest} className="space-y-4 pt-2 border-t border-gray-200">
                <div className="text-sm font-bold text-gray-900 flex items-center justify-between">
                  <span>Mentor Scheduling Controls</span>
                  {editMeetLink && (
                    <a
                      href={`mailto:${selectedRequest.email}?subject=Your Takumi Group Doubt Session Details&body=Hi ${encodeURIComponent(
                        selectedRequest.fullName
                      )},%0D%0A%0D%0AYour doubt clearing session for topic "${encodeURIComponent(
                        selectedRequest.topic
                      )}" has been scheduled!%0D%0A%0D%0ADate: ${encodeURIComponent(editDate)}%0D%0ATime: ${encodeURIComponent(
                        editTime
                      )}%0D%0AMeeting Link: ${encodeURIComponent(
                        editMeetLink
                      )}%0D%0A%0D%0ASee you in the session!%0D%0AInnova8r Team`}
                      className="text-xs font-semibold text-teal-700 hover:underline"
                    >
                      ✉️ Email Invite Shortcut
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                      Session Status
                    </label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                    >
                      <option value="received">Received (New)</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                      Meeting Link (Google Meet / Zoom)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. https://meet.google.com/abc-defg-hij"
                      value={editMeetLink}
                      onChange={(e) => setEditMeetLink(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                      Scheduled Date
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Saturday, Oct 11, 2026"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                      Scheduled Time
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 6:00 PM - 7:00 PM IST"
                      value={editTime}
                      onChange={(e) => setEditTime(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                    Slot / Batch Note
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Upcoming Weekend Batch - Saturday 6:00 PM IST"
                    value={editSlot}
                    onChange={(e) => setEditSlot(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                    Internal Mentor / Admin Notes
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Add internal notes about this student's query, mentor assigned, or preparation required..."
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  ></textarea>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={updating}
                    className="px-5 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                  >
                    {updating ? 'Saving Changes...' : 'Save & Update Session'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakumiGroupDoubts;
